import { CommonModule } from '@angular/common';
import { Component, Inject, inject, signal, Signal, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CalendarIntegration } from '@app/_interfaces/calendar-integration.interface';
import { CacheService } from '@app/_services/cache.service';
import { ConsolidatedApiService } from '@app/_services/consolidated-api.service';
import { CalendarIntegrationStore } from '@app/_stores';
import { getNewId } from '@app/_utils/utils';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import {
  getAuth,
} from 'firebase/auth';
import { CalendarAccountFormComponent } from '../calendar-account-form/calendar-account-form.component';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '@environments/environment';
import { AlertService } from '@app/_services/alert.service';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { StorageService } from '@app/_services/storage.servive';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-calendar-connect',
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './calendar-connect.component.html',
  styleUrl: './calendar-connect.component.scss'
})

export class CalendarConnectComponent implements OnInit, OnDestroy {

  readonly calendarStore = inject(CalendarIntegrationStore);
  unSubscribe = new Subject<void>();
  googleCalendarClientId = environment.googleCalendarClientId;
  microsoftCalendarClientId = environment.microsoftCalendarClientId;
  screenEnterTime: Signal<Date> = signal(new Date());

  constructor(
    private dialogRef: MatDialogRef<CalendarConnectComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { type: 'google' | 'microsoft' },
    private consolidatedApi: ConsolidatedApiService,
    public dialog: MatDialog,
    public cc: CacheService,
    public alertService: AlertService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private modalService: ModalService
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
  }

  connectGoogle() {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.error('User not authenticated with Firebase.');
      return;
    }

    // Load the Google Identity Services script if not already loaded
    if (!document.getElementById('google-identity-script')) {
      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => this.initGoogleAuth(user);
      document.head.appendChild(script);
    } else {
      this.initGoogleAuth(user);
    }
  }

  connectMicrosoft() {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.error('User not authenticated with Firebase.');
      return;
    }

    const domain = window.location.origin;
    const state = crypto.randomUUID();
    const redirectUri = `${domain}/me-calendar-auth`;

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` + new URLSearchParams({
      client_id: this.microsoftCalendarClientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: 'openid email User.Read offline_access Calendars.ReadWrite',
      response_mode: 'query',
      state: state,
      prompt: 'select_account'
    }).toString();

    // Open Microsoft login in a popup
    const width = 500;
    const height = 600;

    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    const popup = window.open(authUrl, '_blank', `width=${width},height=${height},left=${left},top=${top}`);

    // Listen for the postMessage from the popup
    window.addEventListener('message', (event) => {
      if (event.origin !== window.location.origin) return;
      const { code, receivedState } = event.data || {};
      if (code && receivedState === state) {
        const serverAuthCode = code;
        console.log('microsoft response', event);

        const payload = {
          serverAuthCode,
          type: 'microsoft',
          uid: user.uid,
          email: null,
          name: null,
          id: getNewId(),
          redirectUri: redirectUri
        };
        console.log('microsoft payload', payload);
        this.getCalendarDetails(payload);
      }
    });
  }

  initGoogleAuth(user: any) {
    // @ts-ignore - Google Identity Services
    const client = google.accounts.oauth2.initCodeClient({
      client_id: this.googleCalendarClientId,
      scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events email profile',
      ux_mode: 'popup',
      redirect_uri: 'postmessage',
      callback: async (response: { code: string | null }) => {
        if (response.code) {
          console.log('google response', response);
          const serverAuthCode = response.code;
          const payload = {
            serverAuthCode,
            type: 'google',
            uid: this.cc.user.uid || '',
            email: null,
            name: null,
            id: getNewId(),
            redirectUri: 'postmessage'
          };
          console.log('google payload', payload);
          this.getCalendarDetails(payload);
        }
      }
    });

    client.requestCode();
  }

  async getCalendarDetails(credentials: any) {
    try {
      this.cc.isLoading.set(true);
      const result = await this.consolidatedApi.call<{ data: string }>('getCalendarDetails', credentials);
      if (result.error) {
        throw new Error(result.error);
      }
      const calendarDetails = JSON.parse(result.data!.data);
      console.log('calendarDetails', calendarDetails);
      const activeAccounts = this.calendarStore.activeAccounts();
      const existingCalendar = activeAccounts.find((account) => account.email === calendarDetails.email && account.calendarType === calendarDetails.calendarType);
      if (existingCalendar) {
        this.alertService.alert(this.ss.texts()['duplicateCalendarAccount_title'], this.ss.texts()['duplicateCalendarAccount_content'], this.ss.texts()['common_ok']);
        return;
      } else {
        this.openCalendarAccountForm(calendarDetails);
      }
    } catch (error) {
      console.error('❌ Calendar connection failed:', error);
    } finally {
      this.cc.isLoading.set(false);
    }
  }

  openCalendarAccountForm(calendarAccountData: CalendarIntegration) {
    let setupCalendarDialog;
    if (this.cc.isNarrowScreen()) {
      setupCalendarDialog = this.modalService.openMainContent(CalendarAccountFormComponent,
        {
          mode: 'new',
          value: calendarAccountData,
        },
        { disableClose: true, autoFocus: false }
      );
    } else {
      setupCalendarDialog = this.modalService.openLevel1(CalendarAccountFormComponent,
        {
          mode: 'new',
          value: calendarAccountData,
        },
        { disableClose: true, autoFocus: false }
      );
    }

    return this.modalService.getClosedObservable(setupCalendarDialog).pipe(takeUntil(this.unSubscribe)).subscribe(result => {
      if (result) {
        this.dialogRef.close(result);
      } else {
        this.closeDialog();
      }
    });
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('calendarSetup', previousScreen);
      }
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.as.screenExitEvent('calendarSetup', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
